import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";
import { money } from "../lib/format";
import { ArrowRight, Bell } from "lucide-react";

export function Offers({ products, currency }) {
  const { addItem } = useCart();
  const offers = products.filter((p) => p.is_offer && !p.is_upcoming).slice(0, 4);
  if (!offers.length) return null;

  return (
    <section
      id="ofertas"
      data-testid="offers-section"
      className="bg-espresso text-cream py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <span className="text-[11px] uppercase tracking-[0.3em] text-copper">
            Tiempo limitado
          </span>
          <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-none text-cream">
            Ofertas de <span className="italic text-copper">temporada</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-px bg-cream/10 border border-cream/10">
          {offers.map((p, i) => (
            <Reveal
              key={p.id}
              delay={i * 0.08}
              className="bg-espresso p-6 md:p-8 flex gap-6 group"
            >
              <div className="w-28 h-36 md:w-32 md:h-40 overflow-hidden shrink-0 bg-cream/5">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-between py-1">
                <div>
                  <h3 className="font-serif text-2xl text-cream">{p.name}</h3>
                  <p className="text-cream/60 text-sm font-light mt-2 line-clamp-2">
                    {p.description}
                  </p>
                </div>
                <div className="flex items-end justify-between gap-4 mt-4">
                  <div>
                    <span className="text-copper text-lg font-medium">
                      {money(p.offer_price, currency)}
                    </span>
                    <span className="text-cream/40 text-sm line-through ml-2">
                      {money(p.price, currency)}
                    </span>
                  </div>
                  <button
                    data-testid={`offer-add-${p.id}`}
                    onClick={() => addItem(p)}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-cream border-b border-copper pb-1 hover:text-copper transition-colors"
                  >
                    Agregar <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Launches({ products, currency, whatsapp }) {
  const upcoming = products.filter((p) => p.is_upcoming);
  if (!upcoming.length) return null;

  const notify = (name) =>
    `https://wa.me/${whatsapp}?text=${encodeURIComponent(
      `Hola MO by Vir 🌸 Quiero que me avisen por aquí cuando esté disponible: ${name}`
    )}`;

  return (
    <section
      id="lanzamientos"
      data-testid="launches-section"
      className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32"
    >
      <Reveal className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.3em] text-rose">
          En el horizonte
        </span>
        <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-none">
          Próximos <span className="italic text-rose">lanzamientos</span>
        </h2>
        <p className="text-taupe mt-5 font-light">
          Adelántate a lo que viene. Estas piezas llegarán pronto a la boutique virtual.
        </p>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {upcoming.map((p, i) => (
          <motion.div
            key={p.id}
            data-testid={`launch-card-${p.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1 }}
            className="group"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-surface">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover grayscale-[0.15] transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-espresso/10" />
              <span className="absolute top-3 left-3 bg-cream/90 text-espresso text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">
                Próximamente
              </span>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <h3 className="font-serif text-xl">{p.name}</h3>
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-taupe">
                <Bell size={13} /> {money(p.price, currency)}
              </span>
            </div>
            <a
              data-testid={`notify-launch-${p.id}`}
              href={notify(p.name)}
              target="_blank"
              rel="noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-espresso text-espresso rounded-full py-2.5 text-[11px] uppercase tracking-[0.18em] hover:bg-espresso hover:text-cream transition-colors"
            >
              <Bell size={13} /> Avísame por WhatsApp
            </a>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
